import { inject, Injectable } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, exhaustMap, tap, finalize, withLatestFrom, catchError, mergeMap, take } from 'rxjs/operators';
import { AuthActions } from './auth.actions';
import { Store } from '@ngrx/store';
import { GlobalActions } from '../global/global.actions';
import { Router } from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { LOCAL_STORAGE_KEY_AUTH_TOKEN, LOCAL_STORAGE_KEY_USER_NAME } from '@core/constant/constant';
import { EMPTY, of } from 'rxjs';
import { selectRedirectUrlLoginSuccess } from '../global/global.selector';
import { isExternalPricingRoute } from '@core/utils/external-pricing-session';

@Injectable()
export class AuthEffects {
  actions = inject(Actions);
  store = inject(Store);
  router = inject(Router);
  localStorageService = inject(LocalStorageService);
  translate = inject(TranslateService);
  login$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.login),
      tap(() => {
        this.store.dispatch(GlobalActions.setLoading(true));
      }),
      exhaustMap((actions) =>
        this.authService.login(actions.body).pipe(
          map((response) => {
            // Check if password change is required
            if (response.requiresPasswordChange) {
              // Don't save tokens, redirect to change password page
              this.router.navigate(['/change-password'], {
                queryParams: { username: actions.body.userName }
              });
              this.store.dispatch(
                GlobalActions.setMessage({
                  infoMessage: {
                    message: response.message || this.translate.instant('LOGIN.PASSWORD_CHANGE_REQUIRED'),
                    type: 'warning',
                  },
                })
              );
              return AuthActions.loginSuccess(actions.body.userName, response);
            }
            
            if (response.accessToken && response.refreshToken) {
              response.expiry_time = Date.now() + response.expiresIn * 1000;
              this.localStorageService.saveData(
                LOCAL_STORAGE_KEY_AUTH_TOKEN,
                JSON.stringify(response)
              );
              this.localStorageService.saveData(
                LOCAL_STORAGE_KEY_USER_NAME,
                actions.body.userName
              );
            }
            
            return AuthActions.loginSuccess(actions.body.userName, response);
          }),
          tap((action) => {
            // Only show success message and redirect if password change is not required
            if (!action.response.requiresPasswordChange && action.response.accessToken) {
              this.store.dispatch(AuthActions.loadPermissions());
              this.store.dispatch(
                GlobalActions.setMessage({
                  infoMessage: {
                    message: this.translate.instant(
                      'LOGIN.LOGIN_SUCCESS'
                    ),
                    type: 'success',
                  },
                })
              );
              this.store.dispatch(AuthActions.redirectAfterLogin())
            }
          }),
          catchError((error) => {
            this.store.dispatch(GlobalActions.setLoading(false));
            return EMPTY;
          }),
          finalize(() => {
            this.store.dispatch(GlobalActions.setLoading(false));
          })
        )
      )
    )
  );

  entraLogin$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.entraLogin),
      tap(() => {
        this.store.dispatch(GlobalActions.setLoading(true));
      }),
      exhaustMap((actions) =>
        this.authService.entraLogin(actions.body).pipe(
          map((response) => {
            response.expiry_time = Date.now() + response.expiresIn * 1000;
            this.localStorageService.saveData(
              LOCAL_STORAGE_KEY_AUTH_TOKEN,
              JSON.stringify(response)
            );
            this.localStorageService.saveData(
              LOCAL_STORAGE_KEY_USER_NAME,
              ''
            );
            return AuthActions.loginSuccess('', response);
          }),
          tap(() => {
            this.store.dispatch(AuthActions.loadPermissions());
            this.store.dispatch(
              GlobalActions.setMessage({
                infoMessage: {
                  message: this.translate.instant(
                    'LOGIN.LOGIN_SUCCESS'
                  ),
                  type: 'success',
                },
              })
            );
            this.store.dispatch(AuthActions.redirectAfterLogin())
          }),
          catchError(()=>{
            this.router.navigate(['/login']);
            return EMPTY
          }),
          finalize(() => {
            this.store.dispatch(GlobalActions.setLoading(false));
          })
        )
      )
    )
  );

  redirectAfterLogin$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(AuthActions.redirectAfterLogin),
        mergeMap(() =>
          this.store.select(selectRedirectUrlLoginSuccess).pipe(
            take(1),
            tap((url) => {
              this.router.navigate([
                url || '/',
              ]);
              this.store.dispatch(GlobalActions.clearRedirectUrlLoginSuccess());
            })
          )
        )
      ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.refreshToken),
      tap(() => {
        this.store.dispatch(GlobalActions.setLoading(true));
      }),
      exhaustMap(() => {
        const credential = this.localStorageService.getAuthToken();
        if (!credential || !credential.refreshToken) {
          this.localStorageService.removeData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
          this.localStorageService.removeData(LOCAL_STORAGE_KEY_USER_NAME);
          if (!isExternalPricingRoute(this.router.url)) {
            this.router.navigate(['/login']);
          }
          this.store.dispatch(GlobalActions.setLoading(false));
          return EMPTY;
        }
        return this.authService.refreshToken(credential.refreshToken).pipe(
          map((response) => {
            response.expiry_time = Date.now() + response.expiresIn * 1000;
            this.localStorageService.saveData(
              LOCAL_STORAGE_KEY_AUTH_TOKEN,
              JSON.stringify(response)
            );
            this.store.dispatch(AuthActions.loadPermissions());
            return AuthActions.refreshTokenSuccess(response);
          }),
          catchError((error) => {
            console.error('Token refresh failed:', error);
            this.localStorageService.removeData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
            this.localStorageService.removeData(LOCAL_STORAGE_KEY_USER_NAME);
            if (!isExternalPricingRoute(this.router.url)) {
              this.router.navigate(['/login']);
            }
            return EMPTY;
          }),
          finalize(() => {
            this.store.dispatch(GlobalActions.setLoading(false));
          })
        );
      })
    )
  );

  loadPermissions$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.loadPermissions),
      exhaustMap(() =>
        this.authService.getScopes().pipe(
          map((permissions) => {
            return AuthActions.loadPermissionsSuccess(permissions);
          }),
          catchError((error) => {
            console.error('Error loading permissions from GetScopes:', error);
            const credential = this.localStorageService.getAuthToken();
            if (credential?.accessToken) {
              try {
                const payload = JSON.parse(atob(credential.accessToken.split('.')[1]));
                const jwtPermissions: string[] = [];
                if (payload.permission) {
                  if (Array.isArray(payload.permission)) {
                    jwtPermissions.push(...payload.permission);
                  } else {
                    jwtPermissions.push(payload.permission);
                  }
                }
                return of(AuthActions.loadPermissionsSuccess(jwtPermissions));
              } catch (e) {
                console.error('Error extracting permissions from JWT:', e);
              }
            }
            return of(AuthActions.loadPermissionsSuccess([]));
          })
        )
      )
    )
  );

  constructor(private authService: AuthService) {}
}
